import { cn } from "@/lib/utils";
import type { MobileLayoutView } from "@/state/state-default";
import type { LucideIcon } from "lucide-react";
import { CandlestickChart, Layers3, Wallet2 } from "lucide-react";
import { memo, useCallback } from "react";

type MobileBottomNavProps = {
  activeView: MobileLayoutView;
  onChange: (view: MobileLayoutView) => void;
};

const NAV_ITEMS: {
  id: MobileLayoutView;
  label: string;
  icon: LucideIcon;
}[] = [
  {
    id: "markets",
    label: "Markets",
    icon: CandlestickChart,
  },
  {
    id: "trade",
    label: "Trade",
    icon: Layers3,
  },
  {
    id: "account",
    label: "Account",
    icon: Wallet2,
  },
];

// Memoize to prevent re-renders from parent state changes
export const MobileBottomNav = memo(function MobileBottomNav({
  activeView,
  onChange,
}: MobileBottomNavProps) {
  // Use onPointerDown for instant response - fires before onClick
  // This prevents clicks from being "swallowed" during React re-renders
  const handleNavClick = useCallback(
    (itemId: MobileLayoutView) => {
      onChange(itemId);
    },
    [onChange]
  );

  return (
    <nav className="fixed inset-x-0 bottom-0 z-40 border-t border-border/60 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80">
      <div className="mx-auto flex max-w-md items-center justify-around px-4 py-1">
        {NAV_ITEMS.map((item) => {
          const Icon = item.icon;
          const isActive = item.id === activeView;
          return (
            <button
              key={item.id}
              type="button"
              onPointerDown={() => handleNavClick(item.id)}
              className={cn(
                "flex flex-col items-center gap-0.5 rounded-md px-2.5 py-0.5 text-[0.65rem] font-medium transition-colors touch-manipulation select-none",
                isActive
                  ? "text-primary"
                  : "text-muted-foreground hover:text-foreground"
              )}
            >
              <Icon
                className={cn(
                  "h-4 w-4 pointer-events-none",
                  isActive ? "text-primary" : "text-muted-foreground"
                )}
              />
              <span className="pointer-events-none">{item.label}</span>
            </button>
          );
        })}
      </div>
    </nav>
  );
});
